/**
 * Verbindet die Engine mit React. Die Engine hält den Spielzustand als ein veränderliches Objekt;
 * der Store zählt nur eine Version hoch, damit Komponenten nach jedem Zug neu rendern.
 *
 * Gespeichert werden Zustand und Zugprotokoll: lokal (localStorage) immer, in der Cloud (Supabase)
 * wenn angemeldet. Das Protokoll erlaubt Replays, serverseitig geprüfte Highscores und Mehrspieler.
 *
 * Im Mehrspieler-Modus gehen Züge nicht direkt in die Engine, sondern über `moveSink` an den
 * Server; angewendet werden sie erst, wenn sie in verbindlicher Reihenfolge zurückkommen.
 */
import { create } from 'zustand';
import { S, onSaveHook, type GameState } from '../engine/state';
import { beforeAction, trackAction, markAction } from '../lib/track-game';
import { dispatch as engineDispatch, dispatchAs, migrate, journal, LOCAL_ACTIONS, type Action, type ActionName, actions } from '../engine/actions';
import { setActor, type Role } from '../engine/versus';
import { RULES } from '../engine/version';

const KEY = 'kabinett21-v3';
const KEY_J = 'kabinett21-v3-journal';

/** Mehrspieler-Partien werden getrennt vom Einzelspieler-Stand abgelegt. */
let scope: 'sp' | 'mp' = 'sp';
export function setStorageScope(s: 'sp' | 'mp') { scope = s; }
const keys = () => scope === 'sp' ? [KEY, KEY_J] : [KEY + '-mp', KEY_J + '-mp'];

export type Saved = { state: GameState; journal: Action[] };

type GameStore = {
  v: number;
  loaded: boolean;
  dispatch: <K extends ActionName>(name: K, ...args: Parameters<(typeof actions)[K]>) => void;
  /** Wendet einen Zug an, der verbindlich vom Server kam (Mehrspieler). */
  /** Wendet einen Zug an, der verbindlich vom Server kam. `as` ist der Spieler, der ihn gemacht hat. */
  applyRemote: (a: Action, n?: number, as?: Role) => void;
  newGame: (seed?: number) => void;
  loadFromStorage: () => void;
  loadState: (s: GameState, j?: Action[]) => void;
  bump: () => void;
};

let cloudSave: ((saved: Saved) => void) | null = null;
export function setCloudSave(fn: ((saved: Saved) => void) | null) { cloudSave = fn; }

let moveSink: ((a: Action) => void) | null = null;
/** Mehrspieler: Züge an den Server umleiten statt lokal anzuwenden. null = Einzelspieler. */
export function setMoveSink(fn: ((a: Action) => void) | null) { moveSink = fn; }
/** Läuft gerade eine Mehrspieler-Partie (Züge gehen über den Server)? */
export const isRemote = () => moveSink !== null;

/** Rolle dieses Browsers in einer Mehrspieler-Partie (für lokale Züge und Ansichten). */
let localRole: Role = 'host';
export function setLocalRole(r: Role) { localRole = r; setActor(r); }
export const getLocalRole = (): Role => localRole;

/** Nummer des zuletzt angewendeten Server-Zugs. Schützt davor, einen Zug zweimal anzuwenden. */
let remoteN = 0;
export function setRemoteN(n: number) { remoteN = n; }

function persist() {
  try {
    const [k, kj] = keys();
    localStorage.setItem(k, JSON.stringify(S));
    localStorage.setItem(kj, JSON.stringify(journal));
  } catch { /* privater Modus o. ä. */ }
  if (cloudSave && scope === 'sp') cloudSave({ state: S, journal });
}

export const useGame = create<GameStore>((set, get) => ({
  v: 0,
  loaded: false,
  bump: () => set((st) => ({ v: st.v + 1 })),
  dispatch: (name, ...args) => {
    const b = beforeAction();
    if (moveSink && !LOCAL_ACTIONS.has(name)) { moveSink({ type: name, args }); trackAction(name, args, b); return; }
    setActor(localRole);
    engineDispatch(name, ...args);
    trackAction(name, args, b);
    persist();
    get().bump();
  },
  applyRemote: (a, n, as) => {
    if (n != null) { if (n <= remoteN) return; remoteN = n; }
    markAction();
    try { dispatchAs(as ?? 'host', a.type, a.args); }
    catch (e) { console.error('Zug konnte nicht angewendet werden', a, e); }
    persist();
    get().bump();
  },
  // Regelversion ausdrücklich mitgeben, damit sie im Zugprotokoll steht (Bestenliste, Replays)
  newGame: (seed) => { setActor(localRole); engineDispatch('newGame', seed ?? null, RULES); persist(); get().bump(); },
  loadState: (s, j) => {
    if (moveSink) return; // Mehrspieler: Zustand kommt nur aus den Zügen der Partie
    migrate(s);
    journal.length = 0; if (j && j.length) journal.push(...j);
    persist(); set((st) => ({ v: st.v + 1, loaded: true }));
  },
  loadFromStorage: () => {
    let s: GameState | null = null; let j: Action[] = [];
    try {
      const [k, kj] = keys();
      const r = localStorage.getItem(k); if (r) { const p = JSON.parse(r); if (p && p.phase) s = p; }
      const rj = localStorage.getItem(kj); if (rj) { const p = JSON.parse(rj); if (Array.isArray(p)) j = p; }
    } catch { /* ignorieren */ }
    journal.length = 0;
    if (s) { migrate(s); journal.push(...j); } else engineDispatch('newGame', null, RULES); // protokolliert die Regelversion
    set((st) => ({ v: st.v + 1, loaded: true }));
  },
}));

onSaveHook(persist);

/** Liest den aktuellen Zustand; rendert neu, sobald sich die Version ändert. */
export function useS(): GameState { useGame((st) => st.v); return S; }
